import { useCallback } from 'react';
import {
	BN,
	BigNum,
	SettlePnlMode,
	MIN_I64,
	ZERO,
} from '@velocity-exchange/sdk';
import { TransactionInstruction } from '@solana/web3.js';
import { createSettlePnlIx, UIMarket } from '@velocity-exchange/common';
import useVelocityClient from '../useVelocityClient';
import useVelocityClientIsReady from '../useVelocityClientIsReady';
import useCurrentUserAccount from '../useCurrentUserAccount';
import { useGetTxParams } from '../velocityActions/transactions/useGetTxParams';
import { useTxErrorHandler } from '../velocityActions/transactions/useTxErrorHandler';
import { useAutoSignVelocityActionWithFallback } from '../wallets/useAutoSignVelocityActionWithFallback';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
import { notify } from 'src/utils/notifications';
import invariant from 'tiny-invariant';

interface TransferIsolatedPositionMarginParams {
	amount: BigNum;
	marketIndex: number;
	isAdd: boolean;
	isFullWithdrawal: boolean;
	positionBaseAssetAmount: BN;
}

export const useTransferIsolatedPositionMargin = () => {
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const currentUserAccount = useCurrentUserAccount();
	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();

	const transferIsolatedPositionMargin = useAutoSignVelocityActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				params: TransferIsolatedPositionMarginParams
			) => {
				const {
					amount,
					marketIndex,
					isAdd,
					isFullWithdrawal,
					positionBaseAssetAmount,
				} = params;

				invariant(
					velocityClient && velocityClientIsReady,
					'Drift client is required'
				);
				invariant(
					currentUserAccount?.client,
					'User account client is required'
				);

				const action = isAdd ? 'Adding' : 'Removing';

				notify({
					id: toastId,
					type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
					message: isAutoSigned
						? `${action} isolated margin...`
						: `${action} isolated margin`,
					showUntilCancelled: true,
				});

				try {
					const txParams = getTxParams({
						simulatedComputeUnitMultiplier: 1.6,
						useSimulatedComputeUnits: true,
					});

					const subAccountId = currentUserAccount.userId ?? 0;
					const ixs: TransactionInstruction[] = [];

					// Determine the amount to transfer
					const transferAmount = isAdd
						? amount.val
						: isFullWithdrawal && positionBaseAssetAmount.eq(ZERO)
							? MIN_I64
							: amount.val.neg();

					// For removing margin (especially full withdrawal), we may need to settle PNL first
					const needsToSettle =
						!isAdd && (isFullWithdrawal || positionBaseAssetAmount.eq(ZERO));

					if (needsToSettle) {
						const settlePnlIxs = await createSettlePnlIx({
							velocityClient: velocityClient,
							user: currentUserAccount.client,
							marketIndexes: [marketIndex],
							mode: SettlePnlMode.TRY_SETTLE,
							mainSignerOverride: signerWallet.publicKey,
						});
						ixs.push(settlePnlIxs);
					}

					// Create the transfer instruction
					const transferIx =
						await velocityClient.getTransferIsolatedPerpPositionDepositIx(
							transferAmount,
							marketIndex,
							subAccountId,
							!isAdd, // noAmountBuffer for remove operations
							signerWallet.publicKey
						);

					ixs.push(transferIx);

					await signerWallet.signAndSendTransaction(ixs, txParams);

					notify({
						id: toastId,
						type: 'success',
						message: `${UIMarket.createPerpMarket(marketIndex).uiSymbols.marketSymbol} margin ${
							isFullWithdrawal ? 'withdrawn' : 'updated'
						}`,
					});
				} catch (error) {
					console.error('Error transferring isolated position margin', error);
					if (error instanceof FeePayerError) {
						throw error;
					}

					handleTxError(error, toastId);
				}
			},
			[
				velocityClient,
				velocityClientIsReady,
				currentUserAccount?.client,
				currentUserAccount?.userId,
				getTxParams,
				handleTxError,
			]
		)
	);

	return transferIsolatedPositionMargin;
};
